/**
 * Retry Logic with Exponential Backoff
 * Provides resilient API call handling with automatic retries
 */

import { captureException, addBreadcrumb } from './errorTracking';

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in milliseconds (default: 1000) */
  initialDelayMs?: number;
  /** Maximum delay in milliseconds (default: 10000) */
  maxDelayMs?: number;
  /** Backoff multiplier (default: 2) */
  backoffMultiplier?: number;
  /** Add random jitter to prevent thundering herd (default: true) */
  jitter?: boolean;
  /** Function to determine if error is retryable (default: network/5xx errors) */
  isRetryable?: (error: unknown) => boolean;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
  /** Context for error tracking */
  context?: string;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'signal' | 'context'>> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  jitter: true,
  isRetryable: defaultIsRetryable,
};

/**
 * Default function to determine if an error is retryable
 */
/** Check if error indicates rate limiting - used to attach structured data before rethrow */
export function isRateLimitError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  const code = (error as { code?: string }).code?.toLowerCase() || '';
  const httpCode = (error as { httpCode?: number }).httpCode;
  if (httpCode === 429) return true;
  if (code === 'functions/resource-exhausted' || code === 'resource-exhausted') return true;
  if (message.includes('too many requests') || message.includes('rate limit') || message.includes('resource-exhausted')) return true;
  return false;
}

function defaultIsRetryable(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const code = (error as { code?: string }).code?.toLowerCase() || '';
    const httpCode = (error as { httpCode?: number }).httpCode;

    // NEVER retry rate limit errors (429 / resource-exhausted)
    if (httpCode === 429) return false;
    if (code === 'functions/resource-exhausted') return false;
    if (code === 'resource-exhausted') return false;
    if (message.includes('too many requests')) return false;
    if (message.includes('rate limit')) return false;
    if (message.includes('resource-exhausted')) return false;

    // Do NOT retry these auth/validation errors
    if (code === 'functions/unauthenticated') return false;
    if (code === 'functions/permission-denied') return false;
    if (code === 'functions/invalid-argument') return false;
    if (code === 'functions/failed-precondition') return false;
    if (code === 'functions/aborted') return false;
    if (code === 'functions/not-found') return false;

    // Network errors - always retry
    if (message.includes('network') || message.includes('fetch')) return true;
    if (message.includes('timeout')) return true;
    if (code.includes('unavailable')) return true;

    // Firebase-specific retryable errors
    if (code === 'functions/unavailable') return true;
    if (code === 'functions/deadline-exceeded') return true;
    if (code === 'functions/internal') return true;
  }

  return false;
}

/**
 * Calculate delay with optional jitter
 */
function calculateDelay(
  attempt: number,
  initialDelayMs: number,
  maxDelayMs: number,
  backoffMultiplier: number,
  jitter: boolean
): number {
  const exponentialDelay = initialDelayMs * Math.pow(backoffMultiplier, attempt);
  const clampedDelay = Math.min(exponentialDelay, maxDelayMs);
  
  if (jitter) {
    // Add random jitter of ±25%
    const jitterFactor = 0.75 + Math.random() * 0.5;
    return Math.round(clampedDelay * jitterFactor);
  }
  
  return clampedDelay;
}

/**
 * Sleep for the specified duration, respecting abort signal
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    
    const timeoutId = setTimeout(resolve, ms);
    
    signal?.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/**
 * Execute a function with automatic retry and exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // Check if aborted before attempting
      if (options.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      
      const result = await fn();
      
      // Log successful retry for debugging
      if (attempt > 0) {
        addBreadcrumb({
          category: 'retry',
          message: `Succeeded after ${attempt} ${attempt === 1 ? 'retry' : 'retries'}`,
          level: 'info',
          data: { context: options.context, attempt },
        });
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry if aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      
      // Check if we should retry
      const shouldRetry = attempt < config.maxRetries && config.isRetryable(error);
      
      if (shouldRetry) {
        const delay = calculateDelay(
          attempt,
          config.initialDelayMs,
          config.maxDelayMs,
          config.backoffMultiplier,
          config.jitter
        );
        
        addBreadcrumb({
          category: 'retry',
          message: `Attempt ${attempt + 1} failed, retrying in ${delay}ms`,
          level: 'warning',
          data: { 
            context: options.context, 
            error: error instanceof Error ? error.message : String(error),
          },
        });
        
        await sleep(delay, options.signal);
      } else {
        // Final failure - log to error tracking
        if (attempt > 0) {
          captureException(error, {
            action: 'retry_exhausted',
            extra: { context: options.context, attempts: attempt + 1 },
          });
        }
        // Attach structured rate-limit data so UI can detect cooldown
        if (isRateLimitError(lastError) && lastError instanceof Error) {
          (lastError as { httpCode?: number; code?: string }).httpCode = 429;
          (lastError as { httpCode?: number; code?: string }).code = 'resource-exhausted';
        }
        break;
      }
    }
  }

  throw lastError;
}
